import React, { useContext, useEffect, useState } from 'react';
import styles from './PlayerTransactions.module.scss';
import { PropertyType } from 'esa-ui-library/lib/types';
import { Table, Pagination, EmptyState, BoxSuccess, Modal } from 'esa-ui-library';
import { playerPages, PLAYER_TRANSACTIONS_SEARCH_TYPES, useStore } from '../../../common';
import {
	Header,
	PageAuth,
	PageContainer,
	Sidebar,
	SideMenu,
	Page,
	PageHeader,
	GlobalAlert,
	Filters,
} from '../../components';
import { PERMISSIONS } from '../../constants';
import { AuthContext } from '../../../logic';
import { getRows } from './RowProvider';
import { SearchFilter, DateFilter, DevicesFilter, GamesFilter } from '../../components/Filters/components';
import { TransactionStatusFilter } from '../../components/Filters/components/TransactionStatusFilter/TransactionStatusFilter';
import { TransactionTypeFilter } from '../../components/Filters/components/TransactionTypeFilter/TransactionTypeFilter';
import { TableActions } from '../../components/TableActions/TableActions';
import { tableActions } from '../../components/TableActions/TableActions.const';
import { transactionsListMap } from './PlayerTransaction.const';
import { filterTransactionFieldsByEnv, getFiltersUrl } from './PlayerTransactions.helper';
import { getTransactionsExport } from '../../../api';
import { getColumns, getInitialColumns } from '../../../common/services/getColumns';
import { find, isEmpty } from 'lodash';
import { RoundHistory } from './RoundHistory/RoundHistory';

function PlayerTransactions(): JSX.Element {
	const [state, dispatch] = useStore();
	const { transactions, isFilterValid, rows, columns, pagesTotal, currentPage, transactionsLoading, filters, perPage } =
		state;
	const { token, user } = useContext(AuthContext);
	const [selected, setSelected] = useState<PropertyType>({ transaction: null, action: '' });
	const filteredTransactionFields = filterTransactionFieldsByEnv(transactionsListMap);

	const setColumnsConfig = (columns: []) => {
		return columns.map((c) => {
			const fieldPosition = find(transactionsListMap, ['label', c])?.position;
			return {
				label: c,
				cellClassName: fieldPosition ? styles[fieldPosition] : '',
			};
		});
	};

	const onAction = (action: string, transaction: PropertyType): void => {
		switch (action) {
			case 'round_history':
				setSelected({ transaction, action });
				break;
			default:
				break;
		}
	};

	const handleCloseModal = () => {
		setSelected({ transaction: null, action: '' });
	};

	useEffect(() => {
		dispatch('PARTNERS_INIT', user);
		dispatch('FILTERS_INIT', {
			token,
			filters: ['games', 'devices', 'providers', 'transactionTypes', 'transactionStatuses'],
		});
		dispatch('INIT_COLUMNS', {
			columns: getColumns('transactionsList', filteredTransactionFields),
			initialColumns: getInitialColumns(filteredTransactionFields),
		});
	}, []);

	useEffect(() => {
		dispatch('UPDATE_ROWS', getRows(transactions, columns, onAction));
	}, [transactions, columns]);

	const onPageChange = (page: number | string) => {
		dispatch('LOADING', true);
		dispatch('PAGE_UPDATE', { token, page, perPage });
	};

	const onPerPageChange = (value: PropertyType) => {
		const perPage = value[0].value;
		dispatch('LOADING', true);
		dispatch('PER_PAGE', perPage);
		dispatch('PAGE_UPDATE', { token, currentPage, perPage });
	};

	return (
		<>
			<Header dataId='header' />
			<PageAuth permission={PERMISSIONS.canViewOwnDetails}>
				<PageContainer>
					<Sidebar dataId='admin-sidebar'>
						<SideMenu options={playerPages} title='Player Transactions' />
					</Sidebar>
					<Page>
						<PageHeader title='Player Transactions' />
						<section className={styles.PageContent}>
							<Filters>
								<DevicesFilter />
								<GamesFilter enableSelectAll={false} />
								<TransactionStatusFilter />
								<TransactionTypeFilter />
								<div style={{ marginLeft: 'auto' }}>
									<SearchFilter searchTypes={PLAYER_TRANSACTIONS_SEARCH_TYPES} />
								</div>
								<DateFilter fullWidth />
							</Filters>
							{!isFilterValid && !transactionsLoading ? (
								<div className={styles.EmptyBox}>
									<EmptyState
										title='Please enter Transaction ID, Session ID (any), Round ID or Date range to retrieve results'
										icon={<BoxSuccess />}
										dataId='empty-state-component'
									/>
								</div>
							) : (
								<>
									{(rows.length > 0 || transactionsLoading) && (
										<TableActions
											exportData={{
												api: getTransactionsExport,
												csvHeaders: filteredTransactionFields,
												filtersQuery: getFiltersUrl(filters),
											}}
											filters={[tableActions.EXPORT, tableActions.COLUMNS]}
										/>
									)}
									<Table
										titles={setColumnsConfig(columns)}
										data={rows}
										className={styles.TransactionTable}
										rowClassName={styles.TransactionRow}
										isLoading={transactionsLoading}
									/>
									{!!transactions?.length && (
										<Pagination
											showPerPage
											perPage={perPage}
											onPerPage={onPerPageChange}
											total={Math.ceil(pagesTotal / perPage)}
											current={currentPage}
											onChange={onPageChange}
										/>
									)}
								</>
							)}
						</section>
					</Page>
				</PageContainer>
				<GlobalAlert />
			</PageAuth>
			{!isEmpty(selected?.transaction) && (
				<Modal
					dataId='round-history-modal'
					primary
					confirm
					title='Round History'
					submitLabel='Close'
					onCancel={handleCloseModal}
					onSubmit={handleCloseModal}
				>
					<RoundHistory transaction={selected.transaction} />
				</Modal>
			)}
		</>
	);
}

export { PlayerTransactions };
